import { type PropsWithChildren, createContext, useContext } from 'react'

import { useCloseModal } from '@/atoms'
import { tryCloseTransaction } from '@/features/domain/transaction'
import { useAppDispatch } from '@/store'

import { ModalPublicProps } from '../types'

const modeContext = createContext<'create' | 'update' | 'duplicate'>('create')
const cancelContext = createContext<() => void>(() => undefined)
const manageStorageContext = createContext<() => void>(() => undefined)
const storageSaturationContext = createContext(0)

export function ContextProvider(props: PropsWithChildren<ModalPublicProps>) {
  const { children, mode, onManageStorage, storageSaturationPercent } = props

  const onGoBackToEdit = props.mode !== 'duplicate' ? props.onGoBackToEdit : () => undefined

  return (
    <modeContext.Provider value={mode}>
      <manageStorageContext.Provider value={onManageStorage}>
        <storageSaturationContext.Provider value={storageSaturationPercent}>
          <cancelContext.Provider value={onGoBackToEdit}>{children}</cancelContext.Provider>
        </storageSaturationContext.Provider>
      </manageStorageContext.Provider>
    </modeContext.Provider>
  )
}

export function useOnManageStorage() {
  const dispatch = useAppDispatch()

  const onManageStorage = useContext(manageStorageContext)
  const close = useCloseModal()
  return async () => {
    await dispatch(tryCloseTransaction())
    close()
    onManageStorage()
  }
}

export function useOnCancel() {
  const close = useCloseModal()
  const onCancel = useContext(cancelContext)
  return () => {
    onCancel()
    close()
  }
}

export function useStorageSaturationPercent() {
  return useContext(storageSaturationContext)
}

export function useMode() {
  return useContext(modeContext)
}
